// Rules › Campaigns (K-15, Find walk-through items 43-46, step 2.30; docs/specs/FIND_WALKTHROUGH_2026_10.md).
// Pure helpers for the campaigns list and the campaign editor: shares that add up to 100, the plan (new firms a week
// and follow-ups), whether it fits what the mailboxes are already sending, the sequence step cards and the
// per-campaign approvals. The shapes come from
// lib/campaigns.ts (K-08), which this file never changes.
import { followUpsFrom } from "./sectors";
import type { Campaign, CampaignApprovals, CampaignInput, CampaignStatus, CampaignStep } from "./campaigns";
import { KNOWN_RULE_DEFAULTS, reachOf } from "./campaignReach";
import type { JsonObject } from "./types";
import { sizeText } from "./playbook";
import { ownCount } from "./approvalsFind";
import type { ProspectSearch } from "./prospects";
import type { CoreFields } from "./setupFlow";

/** The name shown when the business has no campaigns yet: all its Find work is one campaign. */
export const MAIN_CAMPAIGN = "Main campaign";

/** A stand-in for the business's work before any campaign exists (no id: saving it creates the campaign). */
export function mainCampaign(): Campaign {
  return {
    id: "",
    name: MAIN_CAMPAIGN,
    status: "approved",
    share: 100,
    audience: {},
    card: {},
    steps: [],
    approvals: {},
    search_id: null,
    counts: { planned: 0, in_sequence: 0, engaged: 0, drafts_waiting: 0, hand_waiting: 0, calls_waiting: 0, sent: 0 },
    updated_at: "",
  };
}

/** What the list shows: the campaigns, or the one "Main campaign" when there are none. */
export function listOrMain(list: Campaign[] | null | undefined): Campaign[] {
  const live = (list ?? []).filter((c) => c.status !== "retired");
  const retired = (list ?? []).filter((c) => c.status === "retired");
  return live.length || retired.length ? [...live, ...retired] : [mainCampaign()];
}

export const STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: "Draft",
  proposed: "Waiting for you",
  approved: "Running",
  paused: "Paused",
  retired: "Retired",
};

/** "12 planned · 30 in sequence · 4 engaged" */
export function countsLine(c: Campaign): string {
  const n = c.counts;
  const waitingAll = n.drafts_waiting + n.hand_waiting + n.calls_waiting;
  if (reachOf(c) === "known") {
    // 10b: firms you know are due under the rule, then followed up.
    return [`${(n.due ?? 0).toLocaleString("en-GB")} due`, `${(n.following_up ?? 0).toLocaleString("en-GB")} followed up`,
      `${n.engaged.toLocaleString("en-GB")} engaged`, waitingAll ? `${waitingAll.toLocaleString("en-GB")} waiting for you` : null]
      .filter(Boolean).join(" · ");
  }
  const parts = [
    `${n.planned.toLocaleString("en-GB")} planned`,
    `${n.in_sequence.toLocaleString("en-GB")} in sequence`,
    `${n.engaged.toLocaleString("en-GB")} engaged`,
  ];
  const waiting = n.drafts_waiting + n.hand_waiting + n.calls_waiting;
  if (waiting) parts.push(`${waiting.toLocaleString("en-GB")} waiting for you`);
  return parts.join(" · ");
}

// ------------------------------------------------------------------------------------------------ shares
/** Campaigns that take part of the weekly pace: running ones (and the stand-in Main campaign). */
export function sharing(list: Campaign[]): Campaign[] {
  // A campaign for firms you know has its own daily limit, not a share of the new-business pace (10b).
  return list.filter((c) => c.status === "approved" && reachOf(c) !== "known");
}

export function sharesOf(list: Campaign[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of sharing(list)) out[c.id] = Math.round(c.share);
  return out;
}

export function sharesTotal(shares: Record<string, number>): number {
  return Object.values(shares).reduce((n, v) => n + (Number.isFinite(v) ? v : 0), 0);
}

/** null when the shares can be saved, else what is wrong in plain words. */
export function sharesProblem(shares: Record<string, number>): string | null {
  const vals = Object.values(shares);
  if (!vals.length) return "No running campaigns to share between.";
  if (vals.some((v) => !Number.isInteger(v) || v < 0 || v > 100)) return "Each share is a whole number from 0 to 100.";
  const total = sharesTotal(shares);
  if (total !== 100) return `The shares add up to ${total}%. They need to add up to 100%.`;
  return null;
}

/** 2.30 One campaign's share set to `value`; the others keep their proportions and take the rest, so the total stays
 * 100. Whole numbers, the remainder going to the largest fractions. With the others all at 0 they split the rest evenly. */
export function rebalance(shares: Record<string, number>, id: string, value: number): Record<string, number> {
  const v = Math.max(0, Math.min(100, Math.round(Number.isFinite(value) ? value : 0)));
  const others = Object.keys(shares).filter((k) => k !== id);
  if (!others.length) return { [id]: 100 };
  const rest = 100 - v;
  const base = others.reduce((n, k) => n + Math.max(0, shares[k] ?? 0), 0);
  const raw = others.map((k) => ({ k, x: base > 0 ? (Math.max(0, shares[k] ?? 0) * rest) / base : rest / others.length }));
  const out: Record<string, number> = { [id]: v };
  for (const r of raw) out[r.k] = Math.floor(r.x);
  let left = rest - raw.reduce((n, r) => n + Math.floor(r.x), 0);
  for (const r of [...raw].sort((a, b) => (b.x % 1) - (a.x % 1))) {
    if (left <= 0) break;
    out[r.k] = (out[r.k] ?? 0) + 1;
    left -= 1;
  }
  return out;
}

/** Equal shares that add up to exactly 100 (the first ones get the remainder). */
export function evenShares(ids: string[]): Record<string, number> {
  const out: Record<string, number> = {};
  if (!ids.length) return out;
  const base = Math.floor(100 / ids.length);
  let rest = 100 - base * ids.length;
  for (const id of ids) {
    out[id] = base + (rest > 0 ? 1 : 0);
    rest -= 1;
  }
  return out;
}

/** Spend follows outreach: the same share (spend is per firm contacted). */
export function shareLine(c: Campaign, total: number): string {
  if (reachOf(c) === "known") return `Its own limit: up to ${c.known_rule?.daily_limit ?? 10} firms you know a day, apart from the new-business pace`;
  if (c.status !== "approved") return "No share while it is not running";
  return `${Math.round(c.share)}% of new firms and spend${total === 100 ? "" : " (shares need to add up to 100)"}`;
}

// ------------------------------------------------------------------------------------------------ the plan
/** "Before they reply" (Craig, 1 Nov 2026): new firms a week and the follow-ups, kept on the card as `cadence_plan`.
 * firms_per_week is the campaign's own limit (crm__campaign_quota); the engine starts a fifth of it each working day. */
export interface CadencePlan {
  firms_per_week: number;
  follow_ups: number;
}

export const DEFAULT_PLAN: CadencePlan = { firms_per_week: 20, follow_ups: 3 };
export const MAX_FOLLOW_UPS = 6;
export const MAX_FIRMS_PER_WEEK = 500;

const int = (v: unknown, lo: number, hi: number, d: number) => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.round(n))) : d;
};

export function readPlan(card: JsonObject | null | undefined): CadencePlan {
  const p = (card?.cadence_plan ?? null) as JsonObject | null;
  if (!p || typeof p !== "object") return { ...DEFAULT_PLAN };
  return {
    firms_per_week: int(p.firms_per_week, 1, MAX_FIRMS_PER_WEEK, DEFAULT_PLAN.firms_per_week),
    follow_ups: int(p.follow_ups, 0, MAX_FOLLOW_UPS, DEFAULT_PLAN.follow_ups),
  };
}

/** What crm_campaign_capacity returns: the mailboxes and everything else they are sending for. */
export interface CampaignCapacity {
  mailboxes: { mailbox_id: string; address: string; provider: string; cap: number; held: boolean; busy: boolean; own_sent_7d: number | null }[];
  reply_reserve: number;
  busy: { own_sent_week: number; cold_cap: number };
  instantly: "off" | "overflow" | "always";
  others: { id: string; name: string; firms_per_week: number | null; emails_each: number }[];
}

export interface Room {
  /** Cold emails a working day the mailboxes can send, after the replies' reserve and anything held. */
  per_day: number;
  /** What the other running campaigns plan to send a day. */
  others_per_day: number;
  /** What this campaign's plan needs a day. */
  mine_per_day: number;
  /** New firms a week this campaign could have on its sequence with what is left. */
  firms_room: number;
  fits: boolean;
  /** Mailboxes the owner also sends from, sending at most busy.cold_cap cold a day. */
  busy: string[];
  held: string[];
}

const WORKING_DAYS = 5;

/** Emails each firm gets on this sequence (at least one). */
export function emailsPerFirm(steps: CampaignStep[], plan: CadencePlan): number {
  const n = steps.length ? steps.filter((s) => s.channel === "email").length : 1 + plan.follow_ups;
  return Math.max(1, n);
}

/** Whether the plan fits what the mailboxes can send, given the other running campaigns (the same sums as
 * crm__campaign_daily_cap). Through Instantly for every cold email, only Instantly's room counts. */
export function roomFor(cap: CampaignCapacity, firmsPerWeek: number, emailsEach: number): Room {
  const sending = cap.mailboxes.filter((b) => (cap.instantly === "always" ? b.provider === "instantly" : true));
  const per_day = sending.reduce((n, b) => n + (b.held ? 0 : Math.max(b.cap - cap.reply_reserve, 0)), 0);
  const others_per_day = cap.others.reduce((n, o) => n + ((o.firms_per_week ?? 0) * o.emails_each) / WORKING_DAYS, 0);
  const mine_per_day = (firmsPerWeek * emailsEach) / WORKING_DAYS;
  const free = Math.max(per_day - others_per_day, 0);
  return {
    per_day, others_per_day: Math.round(others_per_day * 10) / 10, mine_per_day: Math.round(mine_per_day * 10) / 10,
    firms_room: Math.floor((free * WORKING_DAYS) / Math.max(1, emailsEach)),
    fits: mine_per_day <= free + 1e-9 && per_day > 0,
    busy: cap.mailboxes.filter((b) => b.busy).map((b) => b.address),
    held: cap.mailboxes.filter((b) => b.held).map((b) => b.address),
  };
}

// ------------------------------------------------------------------------------------------------ sequence
export const CHANNELS: CampaignStep["channel"][] = ["email", "social_dm", "by_hand", "call", "letter", "linkedin"];
export const CHANNEL_NAME: Record<CampaignStep["channel"], string> = {
  email: "Email",
  by_hand: "Message you send yourself",
  social_dm: "Social media DM (Instagram or Facebook)",
  call: "Phone call",
  letter: "Letter",
  linkedin: "LinkedIn",
};

const FOLLOW_UP_LABELS = ["Short follow-up", "Follow-up with a case study", "A different angle", "A useful link, no ask", "Last note: leaving it there", "One more check-in"];
const GAPS = [3, 4, 7, 7, 10, 14];

/** The sequence the guided answers suggest: a first email, then the follow-ups, spaced out. */
export function stepsFromPlan(plan: CadencePlan): CampaignStep[] {
  const out: CampaignStep[] = [{ day: 0, channel: "email", label: "First email" }];
  for (let i = 0; i < plan.follow_ups; i++) out.push({ day: GAPS[i] ?? 14, channel: "email", label: FOLLOW_UP_LABELS[i] ?? "Follow-up" });
  return out;
}

/** The day each step goes out, counted from the first (steps hold days after the previous step). */
export function dayOf(steps: CampaignStep[], i: number): number {
  let d = 0;
  for (let j = 0; j <= i && j < steps.length; j++) d += j === 0 ? 0 : Math.max(0, steps[j]!.day);
  return d;
}

/** What happens after step i. */
export function nextLine(steps: CampaignStep[], i: number): string {
  if (i >= steps.length - 1) return "No reply: the firm moves to longer-term warming (ReConnect).";
  const next = steps[i + 1]!;
  return `No reply: step ${i + 2} (${CHANNEL_NAME[next.channel].toLowerCase()}) on day ${dayOf(steps, i + 1)}.`;
}

export function moveStep(steps: CampaignStep[], i: number, by: -1 | 1): CampaignStep[] {
  const j = i + by;
  if (j < 0 || j >= steps.length) return steps;
  const out = steps.map((s) => ({ ...s }));
  [out[i], out[j]] = [out[j]!, out[i]!];
  out[0]!.day = 0;
  for (let k = 1; k < out.length; k++) if (out[k]!.day <= 0) out[k]!.day = 3;
  return out;
}

export function removeStep(steps: CampaignStep[], i: number): CampaignStep[] {
  const out = steps.filter((_, k) => k !== i).map((s) => ({ ...s }));
  if (out[0]) out[0].day = 0;
  return out;
}

export function addStep(steps: CampaignStep[]): CampaignStep[] {
  return [...steps.map((s) => ({ ...s })), steps.length ? { day: 7, channel: "email", label: "Follow-up" } : { day: 0, channel: "email", label: "First email" }];
}

/** "4 steps over 14 days, 4 emails" */
export function sequenceLine(steps: CampaignStep[]): string {
  if (!steps.length) return "No steps yet";
  const emails = steps.filter((s) => s.channel === "email").length;
  const over = dayOf(steps, steps.length - 1);
  return `${steps.length} ${steps.length === 1 ? "step" : "steps"} over ${over} ${over === 1 ? "day" : "days"}, ${emails} ${emails === 1 ? "email" : "emails"}`;
}

/** Follow-ups before ReConnect, as the steps stand. */
export function followUpsIn(steps: CampaignStep[]): number {
  return Math.max(0, steps.length - 1);
}

// ------------------------------------------------------------------------------------------------ approvals
export function approvalsLine(approvals: CampaignApprovals): string {
  const own = ownCount(approvals);
  return own ? `${own} ${own === 1 ? "setting differs" : "settings differ"} from your Rules` : "Follows your Rules";
}

// ------------------------------------------------------------------------------------------------ saving
/** What saving sends. The plan's firms a week is the campaign's own limit; enrolment starts a fifth of it a run. */
export function toInput(c: Campaign, plan: CadencePlan): CampaignInput {
  const reach = reachOf(c);
  const base = { ...(c.id ? { id: c.id } : {}), name: c.name.trim() || MAIN_CAMPAIGN, reach };
  // 10b: a campaign for firms you know has its rule in place of an offer, follow-ups and approvals.
  if (reach === "known") return { ...base, known_rule: c.known_rule ?? KNOWN_RULE_DEFAULTS };
  return {
    ...base,
    known_rule: reach === "both" ? (c.known_rule ?? KNOWN_RULE_DEFAULTS) : null,
    audience: c.audience,
    card: { ...c.card, cadence_plan: { ...plan } },
    steps: c.steps,
    approvals: c.approvals,
  };
}

/** A new, empty draft. */
export function blankCampaign(name = "New campaign"): Campaign {
  return { ...mainCampaign(), name, status: "draft", share: 0 };
}

// ------------------------------------------------------------------------------------------------ set-up (L-05)
// Round 2 (items 70, 73-76; step 2.30): a new campaign starts from the business's baseline playbook and offer from
// set-up; who to target and the offer can each be copied from another campaign (or the set-up baseline), and a campaign
// made from Find prospects starts its target from that search. All of it is a starting point the owner then edits.
const isObj = (v: unknown): v is JsonObject => typeof v === "object" && v !== null && !Array.isArray(v);
const txt = (v: unknown) => (typeof v === "string" ? v.trim() : "");
const textList = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => (typeof x === "string" ? x.trim() : isObj(x) ? txt(x.name) : "")).filter(Boolean) : []);
const cut = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

/** The audience keys a campaign (a Find play) takes, with their length limits (crm__play_audience_problems). */
export const AUDIENCE_LIMITS = { who: 300, where: 300, size: 120, notes: 1000 } as const;
/** The offer card's text fields (crm__offer_card_problems): 1 to 600 characters each when present. */
export const CARD_TEXT = ["for_whom", "problem_in_their_words", "what_we_offer", "smallest_yes"] as const;

/** Drops empty fields so a half-filled audience or card still saves (the database refuses empty text). */
export function tidyAudience(a: JsonObject): JsonObject {
  const out: JsonObject = {};
  for (const k of Object.keys(AUDIENCE_LIMITS) as (keyof typeof AUDIENCE_LIMITS)[]) {
    const v = txt(a[k]);
    if (v) out[k] = cut(v, AUDIENCE_LIMITS[k]);
  }
  const keys = Array.isArray(a.segment_keys) ? a.segment_keys.filter((k): k is string => typeof k === "string" && /^[a-z0-9][a-z0-9_/.-]{0,79}$/.test(k)).slice(0, 30) : [];
  if (keys.length) out.segment_keys = keys;
  return out;
}

export function tidyCard(c: JsonObject): JsonObject {
  const out: JsonObject = {};
  for (const k of CARD_TEXT) {
    const v = txt(c[k]);
    if (v) out[k] = cut(v, 600);
  }
  const proof = textList(c.proof).map((p) => cut(p, 300)).slice(0, 6);
  if (proof.length) out.proof = proof;
  if (Array.isArray(c.objections)) {
    const obs = c.objections.filter((o): o is JsonObject => isObj(o) && !!txt(o.objection) && !!txt(o.answer))
      .map((o) => ({ objection: cut(txt(o.objection), 400), answer: cut(txt(o.answer), 600) })).slice(0, 8);
    if (obs.length) out.objections = obs;
  }
  const avoid = textList(c.words_to_avoid).map((w) => cut(w, 60)).slice(0, 20);
  if (avoid.length) out.words_to_avoid = avoid;
  return out;
}

/** Who to target from the business's playbook (set-up's ideal customer). */
export function baselineAudience(config: JsonObject | null | undefined): JsonObject {
  const ic = isObj(config?.ideal_customer) ? (config!.ideal_customer as JsonObject) : {};
  const segments = Array.isArray(ic.segments) ? textList(ic.segments) : textList(ic.sectors);
  const markets = Array.isArray(config?.markets) ? (config!.markets as unknown[]).map((m) => (isObj(m) ? txt(m.country) : txt(m))).filter(Boolean) : [];
  const where = textList(ic.geography).length ? textList(ic.geography) : markets;
  const exclude = textList(ic.exclude);
  const currency = txt(config?.currency) || "GBP";
  return tidyAudience({
    who: txt(ic.description) || segments.join(", "),
    where: where.join(", "),
    size: sizeText(ic.size as JsonObject | string | undefined, currency) || txt(ic.revenue),
    notes: exclude.length ? `Not for: ${exclude.join("; ")}.` : "",
  });
}

/** The offer card from set-up: the approved offer workshop card when there is one, else the offer settings. */
export function baselineCard(config: JsonObject | null | undefined): JsonObject {
  const offer = isObj(config?.offer) ? (config!.offer as JsonObject) : {};
  if (isObj(offer.card)) return tidyCard(offer.card);
  const ic = isObj(config?.ideal_customer) ? (config!.ideal_customer as JsonObject) : {};
  return tidyCard({
    for_whom: txt(ic.description),
    what_we_offer: txt(offer.core_offer) || txt(offer.headline),
    smallest_yes: txt(offer.call_to_action) || txt(offer.ask),
  });
}

/** The first small step a cold email asks for, when the business hasn't said its own (fix plan PR 2). */
export const FIRST_ASK = "A 15-minute call to see if it's useful";

/**
 * The offer from what the owner told Ogma about the business (the core the setup function drafts from their one line
 * or their website: what they sell, who they serve, proof). The problem in the buyer's words is never guessed.
 */
export function cardFromCore(core: Pick<CoreFields, "what_we_sell" | "who_we_serve" | "proof"> | null | undefined): JsonObject {
  if (!core) return {};
  return tidyCard({ for_whom: core.who_we_serve, what_we_offer: core.what_we_sell, smallest_yes: txt(core.what_we_sell) ? FIRST_ASK : "", proof: core.proof });
}

/** `into` with each blank field taken from `from`; anything already written stays as it is. */
export function fillBlanks(into: JsonObject, from: JsonObject): JsonObject {
  const out: JsonObject = { ...into };
  for (const [k, v] of Object.entries(from)) {
    const have = out[k];
    if (have === undefined || have === null || (typeof have === "string" && !have.trim()) || (Array.isArray(have) && !have.length)) out[k] = v;
  }
  return out;
}

/** Who to target from a Find prospects search: the request, its countries and regions, its niches and fit criteria. */
export function audienceFromSearch(s: Pick<ProspectSearch, "query" | "plan">): JsonObject {
  const plan = (s.plan ?? {}) as Partial<ProspectSearch["plan"]>;
  const rank = plan.rank;
  const countries = [...new Set([...(plan.countries ?? []), ...(rank?.countries ?? [])])];
  const where = [...countries.map(countryName), ...(plan.regions ?? [])].filter(Boolean).join(", ");
  const must = (rank?.criteria ?? []).filter((c) => c.kind === "must").map((c) => c.label);
  const nice = (rank?.criteria ?? []).filter((c) => c.kind === "nice").map((c) => c.label);
  const notes = [must.length ? `Must have: ${must.join("; ")}.` : "", nice.length ? `Good to have: ${nice.join("; ")}.` : "",
    (plan.exclude_terms ?? []).length ? `Leave out: ${(plan.exclude_terms ?? []).join(", ")}.` : ""].filter(Boolean).join(" ");
  return tidyAudience({
    who: txt(s.query) || txt(plan.title),
    where,
    size: (plan.size_bands ?? []).join(", "),
    notes,
    segment_keys: [...new Set([...(plan.niches ?? []), ...(rank?.niches ?? [])])] as unknown as JsonObject[keyof JsonObject],
  } as JsonObject);
}

const COUNTRY: Record<string, string> = { GB: "United Kingdom", UK: "United Kingdom", IE: "Ireland", US: "United States", IT: "Italy", FR: "France", ES: "Spain", DE: "Germany", PT: "Portugal", NL: "Netherlands", AE: "United Arab Emirates" };
export const countryName = (c: string) => COUNTRY[c.toUpperCase()] ?? c;

/** A campaign made from a search whose target has not been filled in from it yet (only its request is there). */
export function needsSearchSeed(c: Campaign): boolean {
  if (!c.search_id || c.status !== "draft") return false;
  const a = c.audience ?? {};
  return !txt(a.where) && !txt(a.notes) && !(Array.isArray(a.segment_keys) && a.segment_keys.length);
}

/** Where a new campaign's target or offer can start from: set-up's playbook, the search, or another campaign. */
export interface StartFrom {
  key: string;
  label: string;
}

export function startOptions(list: Campaign[], self: string, search: boolean): StartFrom[] {
  return [
    { key: "setup", label: "Your playbook from set-up" },
    ...(search ? [{ key: "search", label: "The search this campaign was made from" }] : []),
    ...list.filter((c) => c.id && c.id !== self && c.status !== "retired").map((c) => ({ key: c.id, label: `Copy from ${c.name}` })),
  ];
}

/**
 * The guided set-up's starting campaign: set-up's target and offer, and the suggested sequence (unsaved until saved),
 * with the sector's number of follow-ups when the business has one (lib/sectors.ts).
 */
export function newCampaign(config: JsonObject | null | undefined, name = "New campaign"): Campaign {
  const plan: CadencePlan = { ...DEFAULT_PLAN, follow_ups: followUpsFrom(config) ?? DEFAULT_PLAN.follow_ups };
  return { ...blankCampaign(name), audience: baselineAudience(config), card: { ...baselineCard(config), cadence_plan: { ...plan } }, steps: stepsFromPlan(plan) };
}

/** Ogma's recommended mix: a social DM a few days after the first email, sent by hand (items 66, 70). */
export function withDm(steps: CampaignStep[]): CampaignStep[] {
  if (steps.some((s) => s.channel === "social_dm")) return steps;
  const out = steps.map((s) => ({ ...s }));
  const dm: CampaignStep = { day: 2, channel: "social_dm", label: "Instagram or Facebook DM" };
  if (!out.length) return [{ day: 0, channel: "email", label: "First email" }, dm];
  out.splice(1, 0, dm);
  if (out[2]) out[2].day = Math.max(1, out[2].day - 2);
  return out;
}

// ------------------------------------------------------------------------------------------------ a waiting change
export interface ChangeRow {
  key: string;
  label: string;
  before: string;
  after: string;
}

const AUDIENCE_LABEL: Record<string, string> = { who: "Who they are", where: "Where", size: "Size", notes: "Notes", segment_keys: "Niches" };
const CARD_LABEL: Record<string, string> = {
  for_whom: "Who it is for", problem_in_their_words: "Their problem", what_we_offer: "What you offer", smallest_yes: "What you're asking for first",
  proof: "Proof", objections: "Objections", words_to_avoid: "Words to avoid",
};

/**
 * A refused save or approval in the owner's words (fix plan PR 1). The database names fields by their keys
 * ("finish the offer card first: smallest_yes must be text of 1 to 600 characters"); the editor shows their labels.
 */
export function campaignErrorText(message: string): string {
  const card = /\b(for_whom|problem_in_their_words|what_we_offer|smallest_yes) must be text of 1 to ([\d,]+) characters/.exec(message);
  if (card) {
    const [, key = "", most = ""] = card;
    return `Add a few words to "${CARD_LABEL[key] ?? key}" (up to ${most} characters), then try again.`;
  }
  const audience = /\b(who|where|notes) must be text of up to ([\d,]+) characters/.exec(message);
  if (audience) {
    const [, key = "", most = ""] = audience;
    return `Keep "${AUDIENCE_LABEL[key] ?? key}" to ${most} characters or fewer.`;
  }
  if (/each proof item must be text/.test(message)) return "Each line of proof needs a few words, up to 300 characters.";
  const rest = message.replace(/^finish the offer card first: /i, "");
  const first = rest.charAt(0).toUpperCase() + rest.slice(1);
  return first.endsWith(".") ? first : `${first}.`;
}

const shown = (v: unknown): string => {
  if (v === undefined || v === null || v === "") return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number") return String(v);
  if (Array.isArray(v)) return v.map((x) => (isObj(x) ? [txt(x.objection), txt(x.answer)].filter(Boolean).join(": ") : shown(x))).filter(Boolean).join("; ");
  return JSON.stringify(v);
};

/** The fields a waiting change alters (who to target and the offer), before and after, in plain words. */
export function changeRows(c: Pick<Campaign, "audience" | "card" | "pending">): ChangeRow[] {
  const p = c.pending;
  if (!p || !isObj(p)) return [];
  const rows: ChangeRow[] = [];
  const diff = (part: "audience" | "card", labels: Record<string, string>, prefix: string) => {
    const after = p[part];
    if (!isObj(after)) return;
    const before = (isObj(c[part]) ? c[part] : {}) as JsonObject;
    for (const k of Object.keys(labels)) {
      const b = shown(before[k]);
      const a = shown(after[k]);
      if (a !== b) rows.push({ key: `${part}.${k}`, label: `${prefix}${labels[k]}`, before: b, after: a });
    }
  };
  diff("audience", AUDIENCE_LABEL, "Target: ");
  diff("card", CARD_LABEL, "Offer: ");
  return rows;
}

/** "who it targets and its offer": what a waiting change touches. */
export function changeWhat(pending: JsonObject | null | undefined): string {
  if (!pending || !isObj(pending)) return "";
  const a = isObj(pending.audience);
  const o = isObj(pending.card);
  return a && o ? "who it targets and its offer" : a ? "who it targets" : o ? "its offer" : "its set-up";
}
